import React, { useRef, useEffect, useState } from 'react';
import { Calendar, MapPin, Ticket, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function UpcomingShows({ shows }) {
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState(null);

  // Auto-scroll sliding effect
  useEffect(() => {
    if (!shows || shows.length <= 1) return;
    const interval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollRef.current.scrollBy({ left: 240, behavior: 'smooth' });
        }
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [shows]);

  if (!shows || shows.length === 0) return null;

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: "easeOut" } }
  };

  const getImageUrl = (img) => {
    if (!img) return 'https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg';
    if (img.startsWith('http://') || img.startsWith('https://')) return img;
    if (img.startsWith('/')) return img;
    return '/' + img;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '11:00 PM';
    if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) return timeStr;
    const [h, m] = timeStr.split(':');
    if (h && m) {
      const hour = parseInt(h, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${m} ${ampm}`;
    }
    return timeStr;
  };

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 mt-3 mb-3">
      {/* Header */}
      <div className="flex justify-between items-center mb-3 md:mb-6 px-1 sm:px-0">
        <h2 className="text-base md:text-xl font-bold text-gray-900 border-l-4 border-[#d33a33] pl-2.5">Upcoming Shows</h2>
        
        {/* Desktop View All Button */}
        <button 
          onClick={() => navigate('/shows')}
          className="hidden sm:flex text-gray-500 hover:text-gray-800 text-xs font-medium border border-gray-200 px-4 py-1.5 rounded-full items-center bg-white shadow-xs hover:bg-gray-50 transition-colors"
        >
          View All &rarr;
        </button>

        {/* Mobile Arrow Button Only */}
        <button 
          onClick={() => navigate('/shows')}
          className="flex sm:hidden w-7 h-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-red-600 hover:border-red-300 transition-colors shadow-xs shrink-0"
          aria-label="View All Shows"
        >
          <ArrowRight size={14} />
        </button>
      </div>

      {/* Horizontal Sliding Carousel */}
      <motion.div
        ref={scrollRef}
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        className="flex overflow-x-auto gap-2.5 sm:gap-3 md:gap-4 pb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] snap-x snap-mandatory"
      >
        {shows.map((show, index) => {
          const cardId = show.product_id || show.id || index;
          const rawDate = show.jatra_date || show.date || show.camp_from;
          const dateObj = rawDate ? new Date(rawDate) : null;
          const displayDate = dateObj && !isNaN(dateObj) 
            ? dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
            : (rawDate || 'Upcoming');

          const timeText = formatTime(show.jatra_time || show.time);
          const dateTimeString = `${displayDate} • ${timeText}`;

          const showTitle = show.jatra_name || show.product_name || show.title || 'Jatra Show';
          const partyName = show.cat_name || show.jatra_party;
          const locationText = show.jatra_location || show.district_name || show.place_name || show.location || 'Odisha';
          const imgSrc = getImageUrl(show.image || show.product_image);
          const isHovered = hoveredId === cardId;

          return (
            <motion.div
              variants={itemVariants}
              key={cardId}
              onMouseEnter={() => setHoveredId(cardId)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => navigate(`/shows/${cardId}`)}
              className="w-[46%] sm:w-[31%] md:w-[23%] lg:w-[19%] min-w-[160px] sm:min-w-[170px] bg-white rounded-2xl overflow-hidden shadow-xs shrink-0 snap-start flex flex-col group cursor-pointer"
            >
              {/* Poster Image */}
              <div className="h-44 sm:h-52 md:h-60 w-full bg-gray-100 relative overflow-hidden">
                <img
                  src={imgSrc}
                  alt={showTitle}
                  className={`w-full h-full object-cover transition-transform duration-500 ${isHovered ? 'scale-105' : 'scale-100'}`}
                  onError={(e) => {
                    e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg';
                  }}
                />

                {/* Category/Party Tag on Top */}
                {partyName && (
                  <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-md text-white text-[8px] sm:text-[9px] font-bold px-1.5 py-0.5 rounded-md truncate max-w-[110px] z-10">
                    {partyName}
                  </div>
                )}

                {/* Hover Overlay with Ticket-Angled Book Now Button */}
                <div className={`absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent transition-opacity duration-300 flex items-end justify-center p-3 pointer-events-none z-20 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                  <div className={`transform transition-transform duration-300 w-full flex justify-center ${isHovered ? 'translate-y-0' : 'translate-y-4'}`}>
                    <div className={`bg-gradient-to-r from-red-600 to-rose-600 text-white font-['Nunito',sans-serif] font-normal text-[11px] sm:text-xs py-1.5 px-3.5 rounded-md flex items-center justify-center gap-1.5 border border-white/20 relative transition-transform ${isHovered ? 'skew-x-0' : '-skew-x-6'}`}>
                      {/* Ticket notches */}
                      <span className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-black/90 rounded-full"></span>
                      <span className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-black/90 rounded-full"></span>
                      <Ticket size={13} className="shrink-0" />
                      <span className="tracking-wide">Book Now</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Show Details */}
              <div className="p-2 sm:p-3 flex flex-col justify-between bg-white flex-1">
                <div>
                  {/* Jatra Show Name */}
                  <h3 className={`text-xs sm:text-sm font-bold transition-colors line-clamp-1 leading-snug ${isHovered ? 'text-red-600' : 'text-gray-900'}`}>
                    {showTitle}
                  </h3>

                  {/* Real Date & Time & Location */}
                  <div className="mt-1.5 sm:mt-2 space-y-1">
                    <p className="text-[11px] sm:text-xs text-gray-600 flex items-center gap-1 font-medium">
                      <Calendar size={12} className="text-red-500 shrink-0" />
                      <span className="truncate">{dateTimeString}</span>
                    </p>
                    <p className="text-[11px] sm:text-xs text-gray-600 flex items-center gap-1 font-medium">
                      <MapPin size={12} className="text-blue-500 shrink-0" />
                      <span className="truncate">{locationText}</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}
